import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNav from '../../components/layout/TopNav';
import api from '../../services/api';
import { useAuthStore } from '../../store';
import type { CoachingNote } from '../../types';
import { UserRole } from '../../types';

const SIGNAL_NAMES: Record<number, string> = {
  1: 'Vehicle Zone Coverage',
  2: 'Salesperson On Camera',
  3: 'Lighting Quality',
  4: 'Camera Stability',
  5: 'Video Duration',
  6: 'Speech Clarity & Pace',
  7: 'Energy & Enthusiasm',
  8: 'Buyer Name Mentioned',
  9: 'Feature Callout Count',
  10: 'Call to Action Present',
};

const CoachingPage: React.FC = () => {
  const navigate = useNavigate();
  const userRole = useAuthStore((s) => s.user?.role);
  const userId = useAuthStore((s) => s.user?.id);
  const isTenantAdmin = userRole === UserRole.TENANT_ADMIN;

  const [notes, setNotes] = useState<CoachingNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [acknowledging, setAcknowledging] = useState<string | null>(null);
  const [filterAcknowledged, setFilterAcknowledged] = useState<boolean | null>(null);

  const perPage = 20;

  const fetchNotes = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const endpoint = isTenantAdmin ? '/coaching/team' : '/coaching/me';
      const res = await api.get(endpoint, { params: { page: p, perPage } });
      setNotes(res.data.data ?? []);
      setTotal(res.data.meta?.total ?? 0);
    } catch {
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [isTenantAdmin]);

  useEffect(() => {
    fetchNotes(page);
  }, [fetchNotes, page]);

  const handleAcknowledge = async (noteId: string) => {
    setAcknowledging(noteId);
    try {
      const res = await api.post(`/coaching/${noteId}/acknowledge`);
      setNotes((prev) => prev.map((n) => n.id === noteId ? res.data.data : n));
    } catch {
      // silently ignore
    } finally {
      setAcknowledging(null);
    }
  };

  const filtered = filterAcknowledged === null
    ? notes
    : notes.filter((n) => n.isAcknowledged === filterAcknowledged);

  const unacknowledgedCount = notes.filter((n) => !n.isAcknowledged).length;
  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 transition-all duration-300 font-sans flex flex-col">
      <TopNav />
      <main className="flex-1 w-full max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 mb-2">
              {isTenantAdmin ? 'Team Coaching' : 'My Coaching'}
            </p>
            <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight uppercase">
              Coaching Notes
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {!isTenantAdmin && unacknowledgedCount > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-200 dark:border-emerald-800/30">
                <span className="material-symbols-outlined text-emerald-500 text-[18px]">school</span>
                <span className="text-[11px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">
                  {unacknowledgedCount} to review
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Filter Bar */}
        <div className="flex items-center gap-2 mb-8">
          {[
            { label: 'All', value: null },
            { label: 'Pending', value: false },
            { label: 'Acknowledged', value: true },
          ].map(({ label, value }) => (
            <button
              key={label}
              type="button"
              onClick={() => setFilterAcknowledged(value)}
              className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${
                filterAcknowledged === value
                  ? 'bg-primary text-white'
                  : 'bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-800 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Notes List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent animate-spin rounded-full" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
              <span className="material-symbols-outlined text-[32px] text-slate-400">school</span>
            </div>
            <p className="text-sm font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest">No coaching notes yet</p>
            <p className="text-xs text-slate-400 dark:text-slate-600 text-center max-w-sm">
              {isTenantAdmin
                ? 'Coaching tips are added automatically after scoring based on the lowest-scoring signal. You can also add manual notes from a video\'s detail page.'
                : 'Coaching notes will appear here after your videos are scored. Check back after uploading your next video.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((note) => (
              <div
                key={note.id}
                className={`bg-white dark:bg-slate-900/60 border transition-all ${
                  note.isAcknowledged
                    ? 'border-slate-100 dark:border-white/5 opacity-60'
                    : 'border-emerald-100 dark:border-emerald-900/30'
                }`}
              >
                <div className="p-6">
                  {/* Meta row */}
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 ${
                      note.source === 'AUTO'
                        ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                        : 'bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-slate-400'
                    }`}>
                      {note.source === 'AUTO' ? 'Auto' : 'Manager Note'}
                    </span>
                    {note.signalId != null && (
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest border border-slate-200 dark:border-slate-700 px-2 py-0.5">
                        {SIGNAL_NAMES[note.signalId] ?? `Signal ${note.signalId}`}
                      </span>
                    )}
                    <span className="ml-auto text-[9px] text-slate-400 dark:text-slate-600 font-medium">
                      {new Date(note.createdAt).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                      })}
                    </span>
                    {note.isAcknowledged && (
                      <span className="flex items-center gap-1 text-[9px] font-black text-emerald-500 uppercase tracking-widest">
                        <span className="material-symbols-outlined text-[14px]">check_circle</span>
                        Done
                      </span>
                    )}
                  </div>

                  {/* Note text */}
                  <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed mb-5">
                    {note.noteText}
                  </p>

                  {/* Actions */}
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => navigate(`/videos/${note.videoId}`)}
                      className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-primary hover:text-primary/80 transition-colors"
                    >
                      <span className="material-symbols-outlined text-[16px]">play_circle</span>
                      View Video
                    </button>

                    {!note.isAcknowledged && note.repId === userId && (
                      <button
                        type="button"
                        onClick={() => handleAcknowledge(note.id)}
                        disabled={acknowledging === note.id}
                        className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors disabled:opacity-50"
                      >
                        {acknowledging === note.id ? (
                          <div className="w-3.5 h-3.5 border-2 border-emerald-500/30 border-t-emerald-500 animate-spin rounded-full" />
                        ) : (
                          <span className="material-symbols-outlined text-[16px]">check_circle</span>
                        )}
                        Mark as done
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-10">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-10 h-10 flex items-center justify-center border border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:border-slate-400 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_left</span>
            </button>
            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest px-4">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-10 h-10 flex items-center justify-center border border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:border-slate-400 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_right</span>
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default CoachingPage;
